// TMOS Activity Service
// Date: 2025-11-17
// Purpose: Frontend service for activity logging and analytics

import api from './api';

export interface ActivityLog {
  id: string;
  user_id?: string;
  user_first_name?: string;
  user_last_name?: string;
  user_email?: string;
  tenant_id: string;
  activity_type: 'LOGIN' | 'LOGOUT' | 'PAGE_VIEW' | 'ERROR';
  page_path?: string;
  page_name?: string;
  ip_address?: string;
  user_agent?: string;
  session_id?: string;
  time_spent_seconds?: number;
  error_occurred: boolean;
  metadata?: any;
  created_at: string;
}

export interface ActivityFilters {
  user_id?: string;
  exclude_user_ids?: string[];
  tenant_id?: string;
  activity_type?: 'LOGIN' | 'LOGOUT' | 'PAGE_VIEW' | 'ERROR';
  page_path?: string;
  ip_address?: string;
  ip_range?: string;
  start_date?: string;
  end_date?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

export interface LoginStats {
  total: number;
  today: number;
  thisWeek: number;
  thisMonth: number;
  trend: Array<{ date: string; count: number }>;
}

export interface ActiveUserStats {
  total: number;
  today: number;
  thisWeek: number;
  thisMonth: number;
  trend: Array<{ date: string; count: number }>;
}

export interface HotPage {
  page_path: string;
  page_name: string;
  visits: number;
  total_time_seconds: number;
  avg_time_seconds: number;
  unique_users: number;
}

export interface ErrorRateStats {
  total: number;
  today: number;
  thisWeek: number;
  thisMonth: number;
  rate: number;
  trend: Array<{ date: string; count: number; rate: number }>;
}

export interface TenantStats {
  tenant_id: string;
  tenant_name: string;
  total_activities: number;
  unique_users: number;
  logins: number;
  page_views: number;
  errors: number;
}

export interface IPStats {
  ip_address: string;
  activities: number;
  unique_users: number;
  logins: number;
  page_views: number;
  errors: number;
  first_seen: string;
  last_seen: string;
}

class ActivityService {
  /**
   * Log page view activity
   */
  async logPageView(
    pagePath: string,
    pageName: string,
    timeSpentSeconds?: number,
    sessionId?: string
  ): Promise<void> {
    try {
      await api.post('/activity/page-view', {
        page_path: pagePath,
        page_name: pageName,
        time_spent_seconds: timeSpentSeconds,
        session_id: sessionId
      });
    } catch (error: any) {
      // Suppress 401 errors (user not authenticated) - this is expected during logout or token expiry
      if (error?.response?.status === 401) {
        // Silently ignore - user is not authenticated, which is fine
        return;
      }
      // Don't fail if logging fails - it's non-critical
      // Only log non-401 errors in development
      if (import.meta.env.DEV) {
        console.error('Failed to log page view:', error);
      }
    }
  }

  /**
   * Log error occurrence
   */
  async logError(pagePath?: string, sessionId?: string): Promise<void> {
    try {
      await api.post('/activity/error', {
        page_path: pagePath,
        session_id: sessionId
      });
    } catch (error: any) {
      // Suppress 401 errors (user not authenticated) - this is expected during logout or token expiry
      if (error?.response?.status === 401) {
        // Silently ignore - user is not authenticated, which is fine
        return;
      }
      // Don't fail if logging fails - it's non-critical
      // Only log non-401 errors in development
      if (import.meta.env.DEV) {
        console.error('Failed to log error:', error);
      }
    }
  }

  /**
   * Get activity logs (Platform Admin only)
   */
  async getActivityLogs(filters: ActivityFilters = {}): Promise<ActivityLog[]> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (key === 'exclude_user_ids' && Array.isArray(value)) {
          // Join array with commas for query parameter
          params.append(key, value.join(','));
        } else {
          params.append(key, value.toString());
        }
      }
    });
    
    const queryString = params.toString();
    const response = await api.get(`/admin/activity/logs${queryString ? `?${queryString}` : ''}`);
    return response.data.data || [];
  }

  /**
   * Get activity statistics (Platform Admin only)
   */
  async getStats(startDate?: string, endDate?: string): Promise<{
    logins: LoginStats;
    activeUsers: ActiveUserStats;
    errorRate: ErrorRateStats;
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    const queryString = params.toString();
    const response = await api.get(`/admin/activity/stats${queryString ? `?${queryString}` : ''}`);
    return response.data.data;
  }

  /**
   * Get hot pages (Platform Admin only)
   */
  async getHotPages(
    startDate?: string,
    endDate?: string,
    sortBy: 'visits' | 'time' = 'visits',
    limit: number = 10
  ): Promise<HotPage[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    params.append('sortBy', sortBy);
    params.append('limit', limit.toString());
    
    const queryString = params.toString();
    const response = await api.get(`/admin/activity/hot-pages?${queryString}`);
    return response.data.data || [];
  }

  /**
   * Get tenant statistics (Platform Admin only)
   */
  async getTenantStats(startDate?: string, endDate?: string): Promise<TenantStats[]> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    const queryString = params.toString();
    const response = await api.get(`/admin/activity/tenant-stats${queryString ? `?${queryString}` : ''}`);
    return response.data.data || [];
  }

  /**
   * Get IP statistics (Platform Admin only)
   */
  async getIPStats(ipRange: string, startDate?: string, endDate?: string): Promise<IPStats[]> {
    const params = new URLSearchParams();
    params.append('ip_range', ipRange);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    const queryString = params.toString();
    const response = await api.get(`/admin/activity/ip-stats?${queryString}`);
    return response.data.data || [];
  }

  /**
   * Get timeline data for charts (Platform Admin only)
   */
  async getTimeline(
    metric: 'logins' | 'activeUsers' | 'errorRate',
    startDate?: string,
    endDate?: string
  ): Promise<Array<{ date: string; count: number; rate?: number }>> {
    const params = new URLSearchParams();
    params.append('metric', metric);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    const queryString = params.toString();
    const response = await api.get(`/admin/activity/timeline?${queryString}`);
    return response.data.data || [];
  }

  /**
   * Export activity data to CSV (Platform Admin only)
   */
  async exportToCSV(filters: ActivityFilters = {}): Promise<Blob> {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, value.toString());
      }
    });
    
    const queryString = params.toString();
    const response = await api.get(`/admin/activity/export${queryString ? `?${queryString}` : ''}`, {
      responseType: 'blob'
    });
    return response.data;
  }
}

export const activityService = new ActivityService();

